/**
 * Image Proxy URL Post-Processor
 * Converts direct image URLs to proxy URLs in HTML content
 * and remints expired signed proxy tokens while the user is in the app.
 */

const REMINT_ATTR = 'data-proxy-reminted';

/**
 * Whether a URL looks like an image-proxy request
 * @param {string} src
 * @returns {boolean}
 */
export function isImageProxySrc(src) {
  if (!src || typeof src !== 'string') {
    return false;
  }
  try {
    const parsed = new URL(src, window.location.origin);
    return parsed.pathname === '/proxy/image' || parsed.pathname.endsWith('/proxy/image');
  } catch (_error) {
    return src.includes('/proxy/image?');
  }
}

/**
 * Preserve size=thumbnail|original from an existing proxy URL onto a reminted URL
 * @param {string} proxyUrl
 * @param {string|null} size
 * @returns {string}
 */
export function applyProxySizeParam(proxyUrl, size) {
  if (!proxyUrl || (size !== 'thumbnail' && size !== 'original')) {
    return proxyUrl;
  }
  try {
    const parsed = new URL(proxyUrl, window.location.origin);
    parsed.searchParams.set('size', size);
    return parsed.toString();
  } catch (_error) {
    const separator = proxyUrl.includes('?') ? '&' : '?';
    return `${proxyUrl}${separator}size=${encodeURIComponent(size)}`;
  }
}

/**
 * Read size query param from a proxy image src
 * @param {string} src
 * @returns {string|null}
 */
export function getProxySizeFromSrc(src) {
  if (!src) {
    return null;
  }
  try {
    const parsed = new URL(src, window.location.origin);
    const size = parsed.searchParams.get('size');
    return size === 'thumbnail' || size === 'original' ? size : null;
  } catch (_error) {
    return null;
  }
}

/**
 * Update enclosing oauth image link to the reminted full-size URL
 * @param {HTMLElement} mediaEl
 * @param {string} proxyUrl
 */
export function updateOauthImageLinkHref(mediaEl, proxyUrl) {
  if (!mediaEl || !proxyUrl || typeof mediaEl.closest !== 'function') {
    return;
  }
  const link = mediaEl.closest('a.oauth-image-link');
  if (!link) {
    return;
  }
  link.setAttribute('href', applyProxySizeParam(proxyUrl, 'original'));
}

/**
 * Process HTML content to convert image URLs to proxy URLs
 * @param {string} html - HTML content with image tags
 * @param {number} userId - Current user ID
 * @param {number|null} postId - Post ID (if images are in a post)
 * @param {string|null} postContext - 'friend', 'group', or null
 * @returns {Promise<string>} HTML with proxy URLs
 */
export async function processImageProxyUrls(html, userId, postId = null, postContext = null) {
  if (!html || !userId) {
    return html;
  }

  try {
    // Find all image tags with data-image-url attributes
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const images = tempDiv.querySelectorAll('img[data-image-url]');

    if (images.length === 0) {
      return html;
    }

    // Collect unique image URLs (https and oauth)
    const imageUrls = Array.from(images)
      .map(img => img.getAttribute('data-image-url'))
      .filter(url => url && (url.startsWith('https://') || url.startsWith('oauth://')));

    if (imageUrls.length === 0) {
      return html;
    }

    const uniqueUrls = [...new Set(imageUrls)];
    const proxyUrlMap = await fetchProxyUrlMap(uniqueUrls, postId, postContext);
    if (!proxyUrlMap) {
      window.logger.warn('Failed to get proxy URLs, using original URLs');
      return html;
    }

    // Replace src attributes with proxy URLs and refresh oauth click targets
    images.forEach(img => {
      const originalUrl = img.getAttribute('data-image-url');
      const proxyUrl = proxyUrlMap.get(originalUrl);
      const size = getProxySizeFromSrc(img.getAttribute('src') || '');

      if (proxyUrl) {
        img.setAttribute('src', applyProxySizeParam(proxyUrl, size));
        updateOauthImageLinkHref(img, proxyUrl);
        // Keep data-image-url for gating/moderation
      }
    });

    return tempDiv.innerHTML;
  } catch (error) {
    window.logger.error('Error processing image proxy URLs:', error);
    // On error, return original HTML
    return html;
  }
}

/**
 * Process a single image URL to get its proxy URL
 * @param {string} imageUrl - Original image URL
 * @param {number} userId - Current user ID
 * @param {number|null} postId - Post ID (optional)
 * @param {string|null} postContext - 'friend', 'group', or null
 * @returns {Promise<string|null>} Proxy URL or null if error
 */
export async function getProxyUrl(imageUrl, userId, postId = null, postContext = null) {
  if (!imageUrl || !userId) {
    return null;
  }

  try {
    const proxyUrlMap = await fetchProxyUrlMap([imageUrl], postId, postContext);
    if (!proxyUrlMap) {
      return null;
    }
    return proxyUrlMap.get(imageUrl) || null;
  } catch (error) {
    window.logger.error('Error getting proxy URL:', error);
    return null;
  }
}

/**
 * Batch-fetch proxy URLs for original image URLs
 * @param {string[]} imageUrls
 * @param {number|null} postId
 * @param {string|null} postContext
 * @returns {Promise<Map<string, string>|null>}
 */
async function fetchProxyUrlMap(imageUrls, postId = null, postContext = null) {
  const body = {
    image_urls: imageUrls,
    post_id: postId
  };
  if (postContext === 'friend' || postContext === 'group') {
    body.post_context = postContext;
  }

  const response = await fetch('/api/images/proxy-urls', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  const proxyUrlMap = new Map();
  (data.proxy_urls || []).forEach(item => {
    if (item.proxy_url) {
      proxyUrlMap.set(item.original_url, item.proxy_url);
    }
  });
  return proxyUrlMap;
}

/**
 * Whether a loaded proxy image looks like the 1x1 error placeholder
 * @param {HTMLImageElement} img
 * @returns {boolean}
 */
export function looksLikeProxyPlaceholder(img) {
  if (!img) {
    return false;
  }
  return img.naturalWidth > 0
    && img.naturalHeight > 0
    && img.naturalWidth <= 1
    && img.naturalHeight <= 1;
}

/**
 * Apply a reminted proxy URL onto a media element's source attributes
 * @param {HTMLElement} el
 * @param {string} proxyUrl
 * @param {'thumbnail'|'original'|null} size
 */
function applyProxyUrlToMediaElement(el, proxyUrl, size) {
  const sizedUrl = applyProxySizeParam(proxyUrl, size);
  if (el.tagName === 'IMG') {
    el.setAttribute('src', sizedUrl);
    updateOauthImageLinkHref(el, proxyUrl);
    return;
  }

  const source = el.querySelector?.('source');
  if (source) {
    source.setAttribute('src', sizedUrl);
  }
  if (el.hasAttribute('src') || !source) {
    el.setAttribute('src', sizedUrl);
  }
  updateOauthImageLinkHref(el, proxyUrl);
}

/**
 * Remint a single proxy image src via the authenticated proxy-urls API
 * @param {HTMLImageElement} img
 * @param {{ postId?: number|null, postContext?: string|null }} options
 * @returns {Promise<boolean>} true if src was updated
 */
export async function remintProxyImage(img, options = {}) {
  if (!img || img.getAttribute(REMINT_ATTR) === '1') {
    return false;
  }

  const originalUrl = img.getAttribute('data-image-url');
  if (!originalUrl || !(originalUrl.startsWith('https://') || originalUrl.startsWith('oauth://'))) {
    return false;
  }

  const { postId = null, postContext = null } = options;

  try {
    const proxyUrlMap = await fetchProxyUrlMap([originalUrl], postId, postContext);
    const proxyUrl = proxyUrlMap?.get(originalUrl);
    if (!proxyUrl) {
      return false;
    }

    const size = getProxySizeFromSrc(img.getAttribute('src') || '');
    applyProxyUrlToMediaElement(img, proxyUrl, size);
    img.setAttribute(REMINT_ATTR, '1');
    return true;
  } catch (error) {
    window.logger?.warn('Failed to remint proxy image URL:', error);
    return false;
  }
}

/**
 * Remint a video or audio element's proxy source URL
 * @param {HTMLVideoElement|HTMLAudioElement} mediaEl
 * @param {{ postId?: number|null, postContext?: string|null }} options
 * @returns {Promise<boolean>} true if source was updated
 */
export async function remintProxyMedia(mediaEl, options = {}) {
  if (!mediaEl || mediaEl.getAttribute(REMINT_ATTR) === '1') {
    return false;
  }

  const originalUrl = mediaEl.getAttribute('data-video-url')
    || mediaEl.getAttribute('data-audio-url')
    || mediaEl.getAttribute('data-image-url');
  if (!originalUrl || !(originalUrl.startsWith('https://') || originalUrl.startsWith('oauth://'))) {
    return false;
  }

  const { postId = null, postContext = null } = options;

  try {
    const proxyUrlMap = await fetchProxyUrlMap([originalUrl], postId, postContext);
    const proxyUrl = proxyUrlMap?.get(originalUrl);
    if (!proxyUrl) {
      return false;
    }

    // Playable streams always need the original file, not a thumbnail
    applyProxyUrlToMediaElement(mediaEl, proxyUrl, 'original');
    mediaEl.setAttribute(REMINT_ATTR, '1');

    if (typeof mediaEl.load === 'function') {
      mediaEl.load();
    }
    return true;
  } catch (error) {
    window.logger?.warn('Failed to remint proxy media URL:', error);
    return false;
  }
}

/**
 * Attach load/error handlers that remint expired proxy tokens once per image
 * @param {ParentNode} root
 * @param {{ postId?: number|null, postContext?: string|null }} options
 */
export function attachProxyImageRemintHandlers(root, options = {}) {
  if (!root || typeof root.querySelectorAll !== 'function') {
    return;
  }

  const images = root.querySelectorAll('img.user-image[data-image-url], img[data-image-url]');
  images.forEach((img) => {
    if (img.dataset.proxyRemintBound === '1') {
      return;
    }
    img.dataset.proxyRemintBound = '1';

    const tryRemint = async (reason) => {
      if (!isImageProxySrc(img.getAttribute('src') || '')) {
        return;
      }
      window.logger?.debug('Reminting proxy image', { reason, src: img.getAttribute('src') });
      await remintProxyImage(img, options);
    };

    img.addEventListener('error', () => {
      tryRemint('error');
    });

    img.addEventListener('load', () => {
      if (looksLikeProxyPlaceholder(img)) {
        tryRemint('placeholder');
      }
    });

    // Already loaded (cached) before handlers attached
    if (img.complete && looksLikeProxyPlaceholder(img) && isImageProxySrc(img.getAttribute('src') || '')) {
      tryRemint('already_loaded_placeholder');
    }
  });

  attachProxyMediaRemintHandlers(root, options);
}

/**
 * Attach error handlers that remint expired proxy tokens for video/audio
 * @param {ParentNode} root
 * @param {{ postId?: number|null, postContext?: string|null }} options
 */
export function attachProxyMediaRemintHandlers(root, options = {}) {
  if (!root || typeof root.querySelectorAll !== 'function') {
    return;
  }

  const mediaElements = root.querySelectorAll(
    'video[data-video-url], video[data-image-url], audio[data-audio-url], audio[data-image-url]'
  );

  mediaElements.forEach((mediaEl) => {
    if (mediaEl.dataset.proxyRemintBound === '1') {
      return;
    }
    mediaEl.dataset.proxyRemintBound = '1';

    const tryRemint = async (reason) => {
      const source = mediaEl.querySelector('source');
      const src = source?.getAttribute('src') || mediaEl.getAttribute('src') || '';
      if (!isImageProxySrc(src)) {
        return;
      }
      window.logger?.debug('Reminting proxy media', { reason, src });
      await remintProxyMedia(mediaEl, options);
    };

    mediaEl.addEventListener('error', () => {
      tryRemint('error');
    });

    const source = mediaEl.querySelector('source');
    if (source) {
      source.addEventListener('error', () => {
        tryRemint('source_error');
      });
    }
  });
}
